import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ListTodo } from "lucide-react";
import type { CardView } from "@shared/api";
import { tasksApi } from "@web/lib/tasksApi";
import { Button } from "@web/ui/Button";
import { Modal } from "@web/ui/Modal";
import { captureTaskFromCard, createTask } from "./taskOps";
import { tasksSearchFor } from "./tasksSearch";
import { taskViewFor } from "./wording";
import { useNow } from "@web/lib/useNow";

export function AddTaskButton({
  threadKey,
  title,
  card,
}: {
  threadKey: string;
  title: string;
  card?: CardView | undefined;
}) {
  const client = useQueryClient();
  const now = useNow(60_000);
  const [open, setOpen] = useState(false);
  const [taskTitle, setTaskTitle] = useState("");
  const [due, setDue] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const existing = useQuery({
    queryKey: ["tasks", "thread", threadKey],
    queryFn: () => tasksApi.forThread(threadKey),
    enabled: open,
  });
  async function add() {
    if (busy || !taskTitle.trim()) return;
    setBusy(true);
    try {
      const details = {
        title: taskTitle.trim(),
        ...(due.trim() ? { due: due.trim() } : {}),
        notes,
      };
      const task = card
        ? await captureTaskFromCard(client, card, { useAction: false, details })
        : await createTask(client, { ...details, threadKey });
      if (task) {
        setOpen(false);
        setNotes("");
        setDue("");
      }
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Button
        size="sm"
        onClick={() => {
          const suggested = card?.actions.find((a) => a.behavior === "create_task")?.params?.title;
          setTaskTitle((suggested || `Follow up: ${title}`).slice(0, 300));
          setOpen(true);
        }}
      >
        <ListTodo aria-hidden className="size-4" />
        Add task
      </Button>
      <Modal
        open={open}
        onClose={() => {
          if (!busy) setOpen(false);
        }}
        label="Add task from email"
      >
        <form
          className="flex w-[min(520px,90vw)] flex-col gap-3 p-5"
          onSubmit={(e) => {
            e.preventDefault();
            void add();
          }}
        >
          <h2 className="text-2xl">Add task from email</h2>
          <p className="text-sm text-muted">{title}</p>
          <p className="text-sm text-muted">
            This adds a to-do linked to the original email. The email stays where it is.
          </p>
          <label className="flex flex-col gap-1">
            Task
            <input
              className="rounded-lg border border-line bg-bg p-2"
              value={taskTitle}
              onChange={(e) => setTaskTitle(e.target.value)}
              maxLength={300}
              required
              disabled={busy}
            />
          </label>
          <label className="flex flex-col gap-1">
            When (optional)
            <input
              className="rounded-lg border border-line bg-bg p-2"
              value={due}
              onChange={(e) => setDue(e.target.value)}
              maxLength={120}
              placeholder="e.g. tomorrow 9am or Friday"
              disabled={busy}
            />
          </label>
          <label className="flex flex-col gap-1">
            Task notes (optional)
            <textarea
              className="rounded-lg border border-line bg-bg p-2"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={20000}
              disabled={busy}
            />
          </label>
          {existing.data?.length ? (
            <div className="flex flex-col gap-1 text-sm">
              <p className="text-muted">Already linked to this email:</p>
              {existing.data.map((t) => (
                <Link
                  key={t.id}
                  to="/tasks"
                  search={tasksSearchFor(taskViewFor(t, now), t.id)}
                  className="text-accent-text underline"
                  onClick={() => setOpen(false)}
                >
                  {t.title}
                </Link>
              ))}
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="primary" busy={busy} disabled={!taskTitle.trim()}>
              Add task
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
          <p className="text-xs text-muted">
            Tasks appear under Tasks. You can edit them there or Undo right after creating one.
          </p>
        </form>
      </Modal>
    </>
  );
}
